/**
 * managerBonuses — Applique les bonus métier des responsables de site
 * (refonte dépôts 17/06/2026, retour user "Ok pour l'amélioration").
 *
 * Les bonus déclarés dans data/depotManagers.js sont désormais appliqués
 * concrètement au revenu / maintenance / carburant. Ils ne s'appliquent
 * QUE pour les véhicules rattachés à un dépôt dont le manager est
 * effectivement recruté et assigné (cf. isDepotOperational).
 *
 * Bonus actifs (volontairement exclus : `flexibility` = satisfaction
 * équipe, jugé trop complexe à intégrer proprement par l'utilisateur) :
 *
 *   • throughput      → +8 % revenu (efficacité chargement) — Claire Fontan
 *   • tier_revenue    → +6 % revenu sur véhicules tier 4-5 — Lukas Müller
 *   • iberic_revenue  → +10 % revenu si destination ES/PT — Sofia Ribeiro
 *   • export_revenue  → +12 % revenu si destination MA / Export — Mehdi
 *   • uk_revenue      → +10 % revenu si destination UK — James Okonkwo
 *   • notoriety       → +4 % multiplicateur notoriété (à terme) — Giulia
 *   • maintenance     → −7 % coût maintenance véhicule — Thierry Mendes
 *   • fuel            → −5 % conso carburant — Anouk Vandersteen
 *   • support_cost    → 0 (déjà reflété par le salaire bas du junior)
 *
 * Multi-bonus : si deux managers s'appliquent (par ex. tier_revenue +
 * iberic_revenue), les multiplicateurs se composent (1.06 × 1.10 = 1.166).
 */
import { getDepotManagerById } from '../data/depotManagers';

/** Helper interne : retourne le manager actif d'un dépôt (null sinon). */
function _activeManagerOfDepot(depotId, ownedDepots, hiredDepotManagers) {
  if (!depotId) return null;
  const depot = (ownedDepots || []).find(d => d.id === depotId);
  if (!depot || depot.isHeadquarters || !depot.managerId) return null;
  if (!(hiredDepotManagers || {})[depot.managerId]) return null;
  return getDepotManagerById(depot.managerId);
}

/**
 * Multiplicateur revenu appliqué à un contrat servi par un véhicule
 * particulier. Retourne 1 si aucun bonus applicable.
 *
 * @param {Object} contract     - { destinationCountry, ... }
 * @param {Object} vehicle      - { tier, depotId, ... }
 * @param {Array}  ownedDepots
 * @param {Object} hiredDepotManagers
 */
export function getManagerRevenueMultiplier(contract, vehicle, ownedDepots, hiredDepotManagers) {
  const mgr = _activeManagerOfDepot(vehicle?.depotId, ownedDepots, hiredDepotManagers);
  if (!mgr || !mgr.bonus) return 1;
  const { type, value } = mgr.bonus;
  let mult = 1;
  // Bonus génériques de revenu.
  if (type === 'throughput') {
    mult *= (1 + (value || 0));
  } else if (type === 'tier_revenue') {
    const tier = vehicle?.tier;
    if (tier === 'T4' || tier === 'T5' || tier === 4 || tier === 5) {
      mult *= (1 + (value || 0));
    }
  } else if (type === 'iberic_revenue') {
    const c = contract?.destinationCountry || contract?.country || '';
    if (c === 'ES' || c === 'PT') {
      mult *= (1 + (value || 0));
    }
  } else if (type === 'export_revenue') {
    const c = contract?.destinationCountry || contract?.country || '';
    if (c === 'MA' || contract?.category === 'Export EU' || contract?.category === 'Export') {
      mult *= (1 + (value || 0));
    }
  } else if (type === 'uk_revenue') {
    const c = contract?.destinationCountry || contract?.country || '';
    if (c === 'UK' || c === 'GB') {
      mult *= (1 + (value || 0));
    }
  }
  return mult;
}

/**
 * Multiplicateur appliqué au coût mensuel de maintenance d'un véhicule.
 * Manager `maintenance` : −7 % sur les camions de son dépôt.
 */
export function getManagerMaintenanceMultiplier(vehicle, ownedDepots, hiredDepotManagers) {
  const mgr = _activeManagerOfDepot(vehicle?.depotId, ownedDepots, hiredDepotManagers);
  if (!mgr || mgr.bonus?.type !== 'maintenance') return 1;
  return 1 - (mgr.bonus.value || 0);
}

/**
 * Multiplicateur appliqué à la conso carburant d'un véhicule (ratio < 1).
 * Manager `fuel` : −5 % sur les camions de son dépôt.
 */
export function getManagerFuelMultiplier(vehicle, ownedDepots, hiredDepotManagers) {
  const mgr = _activeManagerOfDepot(vehicle?.depotId, ownedDepots, hiredDepotManagers);
  if (!mgr || mgr.bonus?.type !== 'fuel') return 1;
  return 1 - (mgr.bonus.value || 0);
}

/**
 * Bonus de notoriété (Giulia Rossi). Renvoie 0 si pas applicable, sinon
 * un multiplicateur appliqué une fois par tick (cap aux 100).
 * Utilisé côté coreSlice.nextDay pour incrementer notoriety.
 */
export function getManagerNotorietyMonthlyBonus(ownedDepots, hiredDepotManagers) {
  let total = 0;
  (ownedDepots || []).forEach(d => {
    const mgr = _activeManagerOfDepot(d.id, ownedDepots, hiredDepotManagers);
    if (mgr?.bonus?.type === 'notoriety') {
      total += mgr.bonus.value || 0;
    }
  });
  return total;
}
